// Shared client-side cache for the site-wide default avatar/banner (admin-configurable at
// /admin/default-images) — fetched once per session and reused by every avatar/banner that
// falls back to it, instead of each one firing its own request.

import { useEffect, useState } from "react"

export interface DefaultProfileImages {
  avatarUrl: string
  bannerUrl: string
}

const FACTORY_DEFAULTS: DefaultProfileImages = {
  avatarUrl: "/branding/defaults/default-profile.webp",
  bannerUrl: "/branding/defaults/default-banner.webp",
}

let cached: DefaultProfileImages | null = null
let inFlight: Promise<DefaultProfileImages> | null = null

export function getDefaultProfileImages(): Promise<DefaultProfileImages> {
  if (cached) return Promise.resolve(cached)
  if (inFlight) return inFlight

  const request: Promise<DefaultProfileImages> = fetch("/api/default-profile-images")
    .then((r) => (r.ok ? r.json() : null))
    .then((data): DefaultProfileImages => {
      const resolved: DefaultProfileImages = data?.config ?? FACTORY_DEFAULTS
      cached = resolved
      return resolved
    })
    .catch((): DefaultProfileImages => {
      cached = FACTORY_DEFAULTS
      return FACTORY_DEFAULTS
    })
    .finally(() => {
      inFlight = null
    })

  inFlight = request
  return request
}

/**
 * React hook wrapper — returns the factory defaults synchronously on first
 * render (so there's never a blank/broken image), then swaps in the real
 * admin-configured URLs once the fetch resolves (typically already cached).
 */
export function useDefaultProfileImages(): DefaultProfileImages {
  const [images, setImages] = useState<DefaultProfileImages>(cached ?? FACTORY_DEFAULTS)

  useEffect(() => {
    let cancelled = false
    getDefaultProfileImages().then((resolved) => {
      if (!cancelled) setImages(resolved)
    })
    return () => { cancelled = true }
  }, [])

  return images
}
